import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { getAllOrdersForAdmin } from '../../services/orderService';
import { productService } from '../../services/productService';
import { inventoryService } from '../../services/inventoryService';
import { orderTrackingService } from '../../services/orderTrackingService';
import { Package, ShoppingCart, AlertCircle, Search, Filter, Eye, Edit, Trash2, Plus, Truck, CheckCircle, Clock, BarChart3, DollarSign, Box, CreditCard, Building2, Send } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { adminOrderService } from '../../services/adminOrderService';
import OrderDetailsModal from './components/OrderDetailsModal';

export default function StoreAdminManagementCenter() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Data states
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [lowStockAlerts, setLowStockAlerts] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalOrders: 0,
    pendingOrders: 0,
    lowStockProducts: 0
  });

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [approvingOrderId, setApprovingOrderId] = useState(null);
  const [approvalToast, setApprovalToast] = useState('');
  const [sendingMysoftId, setSendingMysoftId] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectedOrderForModal, setSelectedOrderForModal] = useState(null);

  useEffect(() => {
    loadDashboardData();
    // One-time fix: update order a2721651 total to 26200 and remove shipping item
    (async () => {
      try {
        const TARGET_ORDER_ID = 'a2721651';
        // Find the order by order_number or id prefix
        const { data: targetOrders } = await supabase
          ?.from('orders')
          ?.select('id, order_number')
          ?.or(`id.ilike.%${TARGET_ORDER_ID}%,order_number.ilike.%${TARGET_ORDER_ID}%`);
        if (targetOrders?.length > 0) {
          const orderId = targetOrders?.[0]?.id;
          // Update total amount
          await supabase?.from('orders')?.update({ total_amount: 26200 })?.eq('id', orderId);
          // Remove shipping/cargo item
          const { data: items } = await supabase
            ?.from('order_items')
            ?.select('id, product:products(title)')
            ?.eq('order_id', orderId);
          if (items) {
            for (const item of items) {
              const title = (item?.product?.title || '')?.toLowerCase();
              if (title?.includes('kargo') || title?.includes('taşıma') || title?.includes('shipping') || title?.includes('cargo') || title?.includes('nakliye')) {
                await supabase?.from('order_items')?.delete()?.eq('id', item?.id);
              }
            }
          }
        }
      } catch (e) {
        console.warn('One-time order fix skipped:', e?.message);
      }
    })();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all data in parallel
      const [ordersData, productsData, alertsData, shipmentsData] = await Promise.all([
        getAllOrdersForAdmin(),
        productService?.getAll(),
        inventoryService?.getLowStockAlerts(),
        orderTrackingService?.getAllShipments()
      ]);

      setOrders(ordersData || []);
      setProducts(productsData || []);
      setLowStockAlerts(alertsData || []);
      setShipments(shipmentsData || []);

      // Calculate stats
      const totalRevenue = ordersData?.reduce((sum, order) => sum + parseFloat(order?.total_amount || 0), 0);
      const pendingOrders = ordersData?.filter(order => order?.status === 'pending')?.length;
      const lowStockProducts = alertsData?.filter(alert => alert?.alertStatus === 'active')?.length;

      setStats({
        totalRevenue,
        totalOrders: ordersData?.length || 0,
        pendingOrders,
        lowStockProducts
      });
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(err?.message || 'Veri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      const { error } = await supabase?.from('orders')?.update({ status: newStatus })?.eq('id', orderId);

      if (error) {
        console.error('Error updating order status:', error);
        alert('Sipariş durumu güncellenemedi: ' + error?.message);
        return;
      }

      // Update local state only after confirmed DB update
      setOrders(prev => prev?.map(order =>
        order?.id === orderId ? { ...order, status: newStatus } : order
      ));

      // Recalculate pending orders stat
      setStats(prev => ({
        ...prev,
        pendingOrders: orders?.filter(o => (o?.id === orderId ? newStatus : o?.status) === 'pending')?.length
      }));
    } catch (err) {
      console.error('Error updating order status:', err);
      alert('Sipariş durumu güncellenemedi');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!confirm('Bu siparişi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.')) return;

    try {
      // Delete order_items first (cascade)
      const { error: itemsError } = await supabase
        ?.from('order_items')
        ?.delete()
        ?.eq('order_id', orderId);

      if (itemsError) {
        console.error('Error deleting order items:', itemsError);
        alert('Sipariş kalemleri silinemedi: ' + itemsError?.message);
        return;
      }

      // Delete the order
      const { error: orderError } = await supabase
        ?.from('orders')
        ?.delete()
        ?.eq('id', orderId);

      if (orderError) {
        console.error('Error deleting order:', orderError);
        alert('Sipariş silinemedi: ' + orderError?.message);
        return;
      }

      // Update local state and recalculate stats
      setOrders(prev => {
        const updated = prev?.filter(o => o?.id !== orderId);
        const totalRevenue = updated?.reduce((sum, o) => sum + parseFloat(o?.total_amount || 0), 0);
        const pendingOrders = updated?.filter(o => o?.status === 'pending')?.length;
        setStats(s => ({ ...s, totalRevenue, totalOrders: updated?.length, pendingOrders }));
        return updated;
      });

      alert('Sipariş başarıyla silindi.');
    } catch (err) {
      console.error('Error deleting order:', err);
      alert('Sipariş silinemedi: ' + err?.message);
    }
  };

  const handleBulkDeleteOrders = async () => {
    if (selectedOrders?.length === 0) return;
    if (!confirm(`Seçili ${selectedOrders?.length} siparişi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`)) return;

    try {
      // Delete order_items for all selected orders
      const { error: itemsError } = await supabase
        ?.from('order_items')
        ?.delete()
        ?.in('order_id', selectedOrders);

      if (itemsError) {
        console.error('Error deleting order items:', itemsError);
        alert('Sipariş kalemleri silinemedi: ' + itemsError?.message);
        return;
      }

      // Delete the orders
      const { error: orderError } = await supabase
        ?.from('orders')
        ?.delete()
        ?.in('id', selectedOrders);

      if (orderError) {
        console.error('Error deleting orders:', orderError);
        alert('Siparişler silinemedi: ' + orderError?.message);
        return;
      }

      setOrders(prev => {
        const updated = prev?.filter(o => !selectedOrders?.includes(o?.id));
        const totalRevenue = updated?.reduce((sum, o) => sum + parseFloat(o?.total_amount || 0), 0);
        const pendingOrders = updated?.filter(o => o?.status === 'pending')?.length;
        setStats(s => ({ ...s, totalRevenue, totalOrders: updated?.length, pendingOrders }));
        return updated;
      });
      setSelectedOrders([]);
      alert(`${selectedOrders?.length} sipariş başarıyla silindi.`);
    } catch (err) {
      console.error('Error bulk deleting orders:', err);
      alert('Siparişler silinemedi: ' + err?.message);
    }
  };

  const getCustomerName = (order) => {
    if (order?.full_name && order?.full_name?.trim() !== '') return order?.full_name;
    // Try parsing shipping_address JSON
    try {
      const addr = typeof order?.shipping_address === 'string'
        ? JSON.parse(order?.shipping_address)
        : order?.shipping_address;
      const name = addr?.full_name || addr?.fullName || addr?.name || addr?.firstName
        ? (addr?.full_name || addr?.fullName || addr?.name || `${addr?.firstName || ''} ${addr?.lastName || ''}`?.trim())
        : null;
      if (name && name?.trim() !== '') return name?.trim();
    } catch (e) {}
    if (order?.user?.full_name) return order?.user?.full_name;
    if (order?.userEmail) return order?.userEmail;
    return 'İsimsiz Müşteri';
  };

  const filteredOrders = orders?.filter(order => {
    const customerName = getCustomerName(order);
    const matchesSearch = customerName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                          order?.id?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order?.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredProducts = products?.filter(product => 
    product?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    product?.sku?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  const handleViewOrder = async (orderId) => {
    const order = orders?.find(o => o?.id === orderId);
    if (!order) {
      navigate(`/order-tracking-dashboard?orderId=${orderId}`);
      return;
    }
    // Fetch order items with product details (fresh from DB)
    try {
      const { data: itemsData } = await supabase
        ?.from('order_items')
        ?.select('*, product:products(id, title, images, price)')
        ?.eq('order_id', orderId);
      // Merge fresh items into order, normalizing to `items` key
      setSelectedOrderForModal({ ...order, items: itemsData || order?.order_items || [] });
    } catch (err) {
      console.error('Error fetching order items:', err);
      // Fallback: use already-loaded order_items, map to `items`
      setSelectedOrderForModal({ ...order, items: order?.order_items || [] });
    }
  };

  const handleApproveBankTransfer = async (orderId) => {
    if (!confirm('Bu EFT/Havale ödemesini onaylamak istediğinizden emin misiniz? Onaylandıktan sonra Mysoft faturası oluşturulacaktır.')) return;

    setApprovingOrderId(orderId);
    try {
      const result = await adminOrderService?.approveBankTransferPayment(orderId);

      // Update local state
      setOrders(prev => prev?.map(order =>
        order?.id === orderId ? { ...order, paymentStatus: 'completed', payment_status: 'completed' } : order
      ));

      const msg = result?.invoiceTriggered
        ? 'Ödeme onaylandı ve fatura Mysoft\'a iletildi.'
        : 'Ödeme onaylandı. (Fatura tetiklenirken bir sorun oluştu, lütfen fatura loglarını kontrol edin.)';

      setApprovalToast(msg);
      setTimeout(() => setApprovalToast(''), 5000);
    } catch (err) {
      console.error('Error approving bank transfer:', err);
      alert('Ödeme onaylanamadı: ' + (err?.message || 'Bilinmeyen hata'));
    } finally {
      setApprovingOrderId(null);
    }
  };

  const handleSendToMysoft = async (orderId) => {
    if (!confirm('Bu siparişi Mysoft\'a göndermek istediğinizden emin misiniz?')) return;
    setSendingMysoftId(orderId);
    try {
      await adminOrderService?.sendToMysoft(orderId);
      setApprovalToast('Sipariş başarıyla Mysoft\'a gönderildi.');
      setTimeout(() => setApprovalToast(''), 5000);
    } catch (err) {
      setApprovalToast(`Mysoft gönderimi başarısız: ${err?.message || 'Bilinmeyen hata'}`);
      setTimeout(() => setApprovalToast(''), 5000);
    } finally {
      setSendingMysoftId(null);
    }
  };

  const handleEditProduct = (productId) => {
    // Navigate to product edit page or open modal
    console.log('Edit product:', productId);
  };

  const handleDeleteProduct = async (productId) => {
    if (!confirm('Bu ürünü silmek istediğinizden emin misiniz?')) return;
    
    try {
      await productService?.deleteProduct(productId);
      await loadDashboardData();
    } catch (err) {
      console.error('Error deleting product:', err);
      alert('Ürün silinemedi');
    }
  };

  const handleToggleSelectOrder = (orderId) => {
    setSelectedOrders(prev =>
      prev?.includes(orderId) ? prev?.filter(id => id !== orderId) : [...prev, orderId]
    );
  };

  const handleSelectAllOrders = (e) => {
    if (e?.target?.checked) {
      setSelectedOrders(filteredOrders?.map(o => o?.id) || []);
    } else {
      setSelectedOrders([]);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-96">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Mağaza Yönetim Merkezi</h1>
          <p className="mt-2 text-gray-600">Resmi mağaza siparişleri, ürünler ve stok yönetimi</p>
        </div>

        {/* Approval Toast */}
        {approvalToast && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
            <p className="text-green-800 font-medium">{approvalToast}</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-800">Hata</h3>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Toplam Gelir</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {stats?.totalRevenue?.toFixed(2)} TL
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Toplam Sipariş</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats?.totalOrders}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <ShoppingCart className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Bekleyen Sipariş</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats?.pendingOrders}</p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Düşük Stok Uyarısı</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats?.lowStockProducts}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'overview' ?'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <BarChart3 className="inline-block h-5 w-5 mr-2" />
                Genel Bakış
              </button>
              <button
                onClick={() => setActiveTab('orders')}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'orders' ?'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <ShoppingCart className="inline-block h-5 w-5 mr-2" />
                Siparişler ({orders?.length})
              </button>
              <button
                onClick={() => setActiveTab('products')}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'products' ?'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Box className="inline-block h-5 w-5 mr-2" />
                Ürünler ({products?.length})
              </button>
              <button
                onClick={() => setActiveTab('inventory')}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'inventory' ?'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Package className="inline-block h-5 w-5 mr-2" />
                Stok Yönetimi
              </button>
              <button
                onClick={() => setActiveTab('shipping')}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'shipping' ?'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Truck className="inline-block h-5 w-5 mr-2" />
                Kargo Takip
              </button>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-gray-900">Dashboard Özeti</h2>
              
              {/* Recent Orders */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Son Siparişler</h3>
                <div className="space-y-3">
                  {orders?.slice(0, 5)?.map(order => (
                    <div key={order?.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <ShoppingCart className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {(order?.full_name && order?.full_name?.trim() !== '') ? order?.full_name : (order?.user?.full_name || (order?.user_id ? 'Kayıtlı Müşteri' : 'Misafir'))}
                          </p>
                          <p className="text-sm text-gray-500">
                            {new Date(order?.created_at)?.toLocaleDateString('tr-TR')}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">{parseFloat(order?.total_amount)?.toFixed(2)} TL</p>
                        <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                          order?.status === 'delivered' ? 'bg-green-100 text-green-800' :
                          order?.status === 'pending'? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {order?.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Low Stock Alerts */}
              {lowStockAlerts?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Düşük Stok Uyarıları</h3>
                  <div className="space-y-3">
                    {lowStockAlerts?.slice(0, 5)?.map(alert => (
                      <div key={alert?.id} className="flex items-center justify-between p-4 border border-red-200 bg-red-50 rounded-lg">
                        <div className="flex items-center gap-4">
                          <AlertCircle className="h-5 w-5 text-red-600" />
                          <div>
                            <p className="font-medium text-gray-900">{alert?.product?.title}</p>
                            <p className="text-sm text-gray-600">
                              Mevcut: {alert?.currentStock} / Eşik: {alert?.threshold}
                            </p>
                          </div>
                        </div>
                        <Button size="sm" onClick={() => setActiveTab('inventory')}>
                          Stok Ekle
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Orders Tab */}
          {activeTab === 'orders' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Tüm Siparişler</h2>
                <div className="flex items-center gap-4">
                  {selectedOrders?.length > 0 && (
                    <button
                      onClick={handleBulkDeleteOrders}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                      Seçilenleri Sil ({selectedOrders?.length})
                    </button>
                  )}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Sipariş ara..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e?.target?.value)}
                      className="pl-10 w-64"
                    />
                  </div>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e?.target?.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">Tüm Durumlar</option>
                    <option value="pending">Beklemede</option>
                    <option value="confirmed">Onaylandı</option>
                    <option value="shipped">Kargoya Verildi</option>
                    <option value="delivered">Teslim Edildi</option>
                    <option value="cancelled">İptal Edildi</option>
                  </select>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={filteredOrders?.length > 0 && selectedOrders?.length === filteredOrders?.length}
                          onChange={handleSelectAllOrders}
                          className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Sipariş ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Müşteri
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tarih
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tutar
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ödeme Yöntemi
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ödeme Durumu
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Durum
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        İşlemler
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredOrders?.map(order => (
                      <tr key={order?.id} className={`hover:bg-gray-50 ${selectedOrders?.includes(order?.id) ? 'bg-red-50' : ''}`}>
                        <td className="px-4 py-4">
                          <input
                            type="checkbox"
                            checked={selectedOrders?.includes(order?.id)}
                            onChange={() => handleToggleSelectOrder(order?.id)}
                            className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {order?.id?.slice(0, 8)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {getCustomerName(order)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(order?.created_at || order?.createdAt)?.toLocaleDateString('tr-TR')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                          {parseFloat(order?.total_amount || order?.totalAmount || 0)?.toFixed(2)} TL
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {(order?.payment_method || order?.paymentMethod) === 'bank_transfer' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-indigo-100 text-indigo-800 font-medium">
                              <Building2 className="h-3 w-3" />
                              EFT / Havale
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 font-medium">
                              <CreditCard className="h-3 w-3" />
                              Kredi Kartı
                            </span>
                          )}
                        </td>
                        <td
                          onClick={async (e) => {
                            e?.stopPropagation();
                            const ps = order?.payment_status || order?.paymentStatus || 'pending';
                            if (ps !== 'paid' && ps !== 'completed' && window.confirm('Bu siparişi ödendi olarak işaretlemek istediğinize emin misiniz?')) {
                              await supabase?.from('orders')?.update({ payment_status: 'paid' })?.eq('id', order?.id);
                              window.location?.reload();
                            }
                          }}
                          className={(() => {
                            const ps = order?.payment_status || order?.paymentStatus || 'pending';
                            return (ps !== 'paid' && ps !== 'completed') ? "px-6 py-4 whitespace-nowrap cursor-pointer hover:opacity-75" : "px-6 py-4 whitespace-nowrap";
                          })()}
                          title={(() => {
                            const ps = order?.payment_status || order?.paymentStatus || 'pending';
                            return (ps !== 'paid' && ps !== 'completed') ? "Ödendi olarak işaretlemek için tıklayın" : "";
                          })()}
                        >
                          {(() => {
                            const ps = order?.payment_status || order?.paymentStatus || 'pending';
                            if (ps === 'paid' || ps === 'completed') {
                              return (
                                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 font-semibold">
                                  <CheckCircle className="h-3 w-3" />
                                  Ödendi
                                </span>
                              );
                            }
                            if (ps === 'pending' || ps === 'unpaid') {
                              return (
                                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-800 font-semibold">
                                  <Clock className="h-3 w-3" />
                                  Bekliyor (Tıkla)
                                </span>
                              );
                            }
                            if (ps === 'failed') {
                              return (
                                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-red-100 text-red-800 font-semibold">
                                  <AlertCircle className="h-3 w-3" />
                                  Başarısız
                                </span>
                              );
                            }
                            return (
                              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700 font-medium">
                                {ps}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                            order?.status === 'delivered' ? 'bg-green-100 text-green-800' :
                            order?.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            order?.status === 'cancelled'? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
                          }`}>
                            {order?.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleViewOrder(order?.id)}
                              className="text-blue-600 hover:text-blue-800"
                              title="Görüntüle"
                            >
                              <Eye className="h-5 w-5" />
                            </button>
                            {(order?.payment_method || order?.paymentMethod) === 'bank_transfer' &&
                              (order?.payment_status || order?.paymentStatus) !== 'completed' && (
                              <button
                                onClick={() => handleApproveBankTransfer(order?.id)}
                                disabled={approvingOrderId === order?.id}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                title="Ödemeyi Onayla"
                              >
                                {approvingOrderId === order?.id ? (
                                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <CheckCircle className="h-3 w-3" />
                                )}
                                Ödemeyi Onayla
                              </button>
                            )}
                            <button
                              onClick={() => handleSendToMysoft(order?.id)}
                              disabled={sendingMysoftId === order?.id}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-purple-600 hover:bg-purple-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              title="Mysoft'a Gönder"
                            >
                              {sendingMysoftId === order?.id ? (
                                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <Send className="h-3 w-3" />
                              )}
                              Mysoft
                            </button>
                            <button
                              onClick={() => handleDeleteOrder(order?.id)}
                              className="text-red-600 hover:text-red-800"
                              title="Sil"
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Products Tab */}
          {activeTab === 'products' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Ürün Yönetimi</h2>
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Ürün ara..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e?.target?.value)}
                      className="pl-10 w-64"
                    />
                  </div>
                  <Button>
                    <Plus className="h-5 w-5 mr-2" />
                    Yeni Ürün
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProducts?.map(product => (
                  <div key={product?.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="aspect-square bg-gray-100 rounded-lg mb-4 overflow-hidden">
                      <img
                        src={product?.primaryImage || '/assets/images/no_image.png'}
                        alt={product?.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">{product?.title}</h3>
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-lg font-bold text-blue-600">{product?.price?.toFixed(2)} TL</p>
                      <p className="text-sm text-gray-600">Stok: {product?.stockQuantity}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleEditProduct(product?.id)}>
                        <Edit className="h-4 w-4 mr-1" />
                        Düzenle
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDeleteProduct(product?.id)}>
                        <Trash2 className="h-4 w-4 mr-1" />
                        Sil
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Inventory Tab */}
          {activeTab === 'inventory' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-gray-900">Stok Yönetimi</h2>
              
              {lowStockAlerts?.length > 0 ? (
                <div className="space-y-4">
                  {lowStockAlerts?.map(alert => (
                    <div key={alert?.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-red-100 rounded-lg">
                          <AlertCircle className="h-6 w-6 text-red-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{alert?.product?.title}</p>
                          <p className="text-sm text-gray-600">
                            Mevcut Stok: {alert?.currentStock} / Eşik: {alert?.threshold}
                          </p>
                        </div>
                      </div>
                      <Button size="sm">
                        Stok Ekle
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <CheckCircle className="mx-auto h-12 w-12 text-green-600 mb-4" />
                  <p className="text-gray-600">Tüm ürünler yeterli stok seviyesinde</p>
                </div>
              )}
            </div>
          )}

          {/* Shipping Tab */}
          {activeTab === 'shipping' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-gray-900">Kargo Takip</h2>
              
              <div className="space-y-4">
                {shipments?.slice(0, 10)?.map(shipment => (
                  <div key={shipment?.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Truck className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Takip No: {shipment?.trackingNumber}</p>
                        <p className="text-sm text-gray-600">
                          Sipariş: {shipment?.orderId?.slice(0, 8)} | {shipment?.carrier?.name}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                        shipment?.status === 'delivered' ? 'bg-green-100 text-green-800' :
                        shipment?.status === 'in_transit'? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {shipment?.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Order Details Modal */}
      {selectedOrderForModal && (
        <OrderDetailsModal
          order={selectedOrderForModal}
          onClose={() => setSelectedOrderForModal(null)}
          onUpdateStatus={(orderId, newStatus) => {
            setOrders(prev => prev?.map(o => o?.id === orderId ? { ...o, status: newStatus } : o));
            setSelectedOrderForModal(prev => prev ? { ...prev, status: newStatus } : null);
          }}
        />
      )}
    </div>
  );
}